/**
 * Main App Component
 * Simple client-side router for the headless Shopify store
 */

import { useState, useEffect } from 'react';
import { HomePage } from './pages/HomePage';
import { CollectionPage } from './pages/CollectionPage';
import { ProductPage } from './pages/ProductPage';
import { ProductsPage } from './pages/ProductsPage';
import { InfoPage } from './pages/InfoPage';
import { AboutPage } from './pages/AboutPage';
import { SupportPage } from './pages/SupportPage';
import { ContactPage } from './pages/ContactPage';
import { FAQPage } from './pages/FAQPage';
import { BlogPage } from './pages/BlogPage';
import { AccountPage } from './pages/AccountPage';
import { SearchPage } from './pages/SearchPage';
import { CookieBanner } from './components/ui/CookieBanner';

// List of informational page slugs (generic pages that use InfoPage component)
const INFO_PAGES = [
  'shipping',
  'returns',
  'warranty',
  'privacy',
  'terms',
  'cookies',
  'compatibility',
  'setup',
  'careers',
  'partners',
];

// Pages with dedicated premium designs
const PREMIUM_PAGES: Record<string, React.ComponentType> = {
  about: AboutPage,
  support: SupportPage,
  contact: ContactPage,
  faq: FAQPage,
  account: AccountPage,
};

// Navigation helper for client-side routing
export function navigate(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// Simple path-based router
function useRoute() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setPath(window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    // Intercept link clicks for client-side navigation
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');

      if (
        anchor &&
        anchor.href &&
        anchor.href.startsWith(window.location.origin) &&
        !anchor.hasAttribute('download') &&
        !anchor.hasAttribute('target') &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        e.button === 0
      ) {
        e.preventDefault();
        const url = new URL(anchor.href);
        if (url.pathname !== window.location.pathname) {
          window.history.pushState({}, '', url.pathname + url.search);
          setPath(url.pathname);
          window.scrollTo(0, 0);
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleClick);
    };
  }, []);

  return path;
}

export function App() {
  const path = useRoute();

  return (
    <>
      <AppRoutes path={path} />
      <CookieBanner />
    </>
  );
}

export function AppRoutes({ path }: { path: string }) {
  // Route matching
  if (path === '/' || path === '') {
    return <HomePage />;
  }

  // Search results page
  if (path === '/search') {
    const params = new URLSearchParams(window.location.search);
    return <SearchPage query={params.get('q') || ''} />;
  }

  // Collection routes
  const collectionMatch = path.match(/^\/collections\/([^/]+)$/);
  if (collectionMatch) {
    return <CollectionPage handle={collectionMatch[1]} />;
  }

  // All products page
  if (path === '/products') {
    return <ProductsPage />;
  }

  // Product routes
  const productMatch = path.match(/^\/products\/([^/]+)$/);
  if (productMatch) {
    return <ProductPage handle={productMatch[1]} />;
  }

  // Blog routes
  if (path === '/blog') {
    return <BlogPage />;
  }
  const blogPostMatch = path.match(/^\/blog\/([^/]+)$/);
  if (blogPostMatch) {
    return <BlogPage slug={blogPostMatch[1]} />;
  }

  // Premium pages with dedicated designs (about, support, contact)
  const pageMatch = path.match(/^\/([^/]+)$/);
  if (pageMatch) {
    const slug = pageMatch[1];

    // Check for premium page first
    const PremiumPage = PREMIUM_PAGES[slug];
    if (PremiumPage) {
      return <PremiumPage />;
    }

    // Fall back to generic info page
    if (INFO_PAGES.includes(slug)) {
      return <InfoPage slug={slug} />;
    }
  }

  // 404 fallback
  return (
    <div className="min-h-screen bg-surface-black flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-display font-bold text-accent-white mb-4">404</h1>
        <p className="text-accent-silver mb-8">Page not found</p>
        <a href="/" className="btn-primary">
          Go Home
        </a>
      </div>
    </div>
  );
}
